import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import ChartTooltip from '../chartTooltip';

export default function DonutChart({
  data = [],
  total = 0,
  showCenterValue = true,
  maxWidth = 300,
  minimumLabelPercentage = 3,
}) {
  const intl = useIntl();
  const hasData = data.some(item => Number(item?.value) > 0);
  const chartData = hasData
    ? data
    : [{
      name: intl.formatMessage({ id: 'dashboard.new.operation.noData' }),
      value: 1,
      color: '#e8edf3',
    }];

  function renderPieLabel({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percentage,
    percent,
  }) {
    const percentageLabel = percentage ?? Math.round(percent * 100);

    if (
      !Number.isFinite(Number(percentageLabel))
      || Number(percentageLabel) < minimumLabelPercentage
    ) {
      return null;
    }

    const radius =
      innerRadius + (outerRadius - innerRadius) * 0.55;

    const x =
      cx +
      radius *
      Math.cos((-midAngle * Math.PI) / 180);

    const y =
      cy +
      radius *
      Math.sin((-midAngle * Math.PI) / 180);

    return (
      <text
        x={x}
        y={y}
        fill="#ffffff"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="11"
        fontWeight="700"
      >
        {`${percentageLabel}%`}
      </text>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth,
        aspectRatio: '1 / 1',
        mx: 'auto',
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={showCenterValue ? '46%' : 0}
            outerRadius="90%"
            paddingAngle={0}
            stroke="#ffffff"
            strokeWidth={1}
            labelLine={false}
            label={hasData ? renderPieLabel : false}
            isAnimationActive={hasData}
          >
            {chartData.map((item) => (
              <Cell
                key={item.name}
                fill={item.color}
              />
            ))}
          </Pie>

          {hasData && (
            <Tooltip
              content={({ active, payload }) => {
                const item = payload?.[0]?.payload;
                const percentage = Number(item?.percentage);

                return (
                  <ChartTooltip
                    active={active}
                    payload={payload}
                    label={item?.name}
                    secondaryText={Number.isFinite(percentage)
                      ? `${percentage.toFixed(2)}%`
                      : null}
                  />
                );
              }}
              wrapperStyle={{ zIndex: 2 }}
            />
          )}

        </PieChart>
      </ResponsiveContainer>

      {showCenterValue && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold">
            {total}
          </Typography>

          <Typography
            variant="subtitle1"
          >
            {intl.formatMessage({ id: 'dashboard.new.operation.total' })}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
